import { API_BASE_URL } from "src/config/apiBase";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  X,
  Play,
  Pause,
  Repeat,
  Pencil,
  Trash2,
  FastForward,
  ExternalLink,
  AlertCircle,
} from "lucide-react";
import Loader from "../Loader";
import { decryptToken } from "../utils/getToken";
import {
  formatInr,
  formatScheduleDate,
  frequencyLabel,
  getAttentionReasons,
} from "./recurringInvoiceUtils";

export default function RecurringScheduleDetailDrawer({
  template,
  open,
  onClose,
  actionId,
  onRunNow,
  onPause,
  onResume,
  onCatchUp,
  onDelete,
}) {
  const navigate = useNavigate();
  const url = API_BASE_URL;
  const token = decryptToken("accessToken") || "";

  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  useEffect(() => {
    if (!open || !template?.id) {
      setHistory([]);
      return;
    }

    let cancelled = false;
    const fetchHistory = async () => {
      setHistoryLoading(true);
      try {
        const res = await fetch(
          `${url}/newinvoice/recurring/${template.id}/history?limit=50`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const data = await res.json();
        if (!cancelled) setHistory(Array.isArray(data) ? data : []);
      } catch {
        if (!cancelled) setHistory([]);
      } finally {
        if (!cancelled) setHistoryLoading(false);
      }
    };

    fetchHistory();
    return () => {
      cancelled = true;
    };
  }, [open, template?.id, url, token]);

  if (!open || !template) return null;

  const reasons = getAttentionReasons(template);
  const busy = actionId === template.id;

  const automationFlags = [
    ["Email customer", template.send_to_customer],
    ["WhatsApp", template.send_whatsapp],
    ["Pre-run notification", template.send_prerun_notification],
    ["Refresh catalog prices", template.refresh_prices_from_catalog],
    ["Payment reminders", template.send_payment_reminders],
    ["Skip if previous unpaid", template.skip_if_previous_unpaid],
  ];

  return (
    <>
      <button
        type="button"
        className="recurring-detail__backdrop"
        aria-label="Close schedule details"
        onClick={onClose}
      />
      <aside className="recurring-detail" role="dialog" aria-modal="true" aria-label="Schedule details">
        <header className="recurring-detail__header">
          <div>
            <p className="recurring-detail__eyebrow">Auto-billing schedule</p>
            <h2>{template.template_name}</h2>
            <p className="recurring-detail__customer">{template.customer_name}</p>
          </div>
          <button type="button" className="recurring-detail__close" onClick={onClose} aria-label="Close">
            <X size={20} />
          </button>
        </header>

        <div className="recurring-detail__body">
          {reasons.length > 0 && (
            <div className="recurring-detail__alert">
              <AlertCircle size={16} />
              <div>
                <strong>Needs attention</strong>
                <ul>
                  {reasons.map((r) => (
                    <li key={r}>{r}</li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          <section className="recurring-detail__section">
            <h3>Schedule</h3>
            <dl className="recurring-detail__grid">
              <div>
                <dt>Status</dt>
                <dd className="recurring-detail__capitalize">{template.status || "active"}</dd>
              </div>
              <div>
                <dt>Frequency</dt>
                <dd>{frequencyLabel(template)}</dd>
              </div>
              <div>
                <dt>Amount</dt>
                <dd>{formatInr(template.grand_total)}</dd>
              </div>
              <div>
                <dt>Next run</dt>
                <dd>{formatScheduleDate(template.next_run_date)}</dd>
              </div>
              <div>
                <dt>End date</dt>
                <dd>{formatScheduleDate(template.end_date)}</dd>
              </div>
              <div>
                <dt>Runs</dt>
                <dd>{template.runs_count || 0}</dd>
              </div>
              <div>
                <dt>Invoice as</dt>
                <dd>{template.invoice_dpstatus || "Published"}</dd>
              </div>
              {template.delivery_status_label && (
                <div>
                  <dt>Last delivery</dt>
                  <dd>{template.delivery_status_label}</dd>
                </div>
              )}
            </dl>
          </section>

          <section className="recurring-detail__section">
            <h3>Automation</h3>
            <ul className="recurring-detail__flags">
              {automationFlags.map(([label, enabled]) => (
                <li key={label} className={enabled ? "is-on" : "is-off"}>
                  {label}
                </li>
              ))}
            </ul>
          </section>

          <section className="recurring-detail__section">
            <div className="recurring-detail__section-head">
              <h3>Generation history</h3>
              <span>{history.length} invoice{history.length !== 1 ? "s" : ""}</span>
            </div>
            {historyLoading ? (
              <Loader />
            ) : history.length === 0 ? (
              <p className="recurring-detail__empty">No invoices generated yet.</p>
            ) : (
              <ul className="recurring-detail__history">
                {history.map((inv) => (
                  <li key={inv.id}>
                    <button
                      type="button"
                      className="recurring-detail__history-link"
                      onClick={() => navigate(`/invoice-detail/${inv.id}`)}
                    >
                      {inv.invoice_number}
                      <ExternalLink size={12} />
                    </button>
                    <span>{formatScheduleDate(inv.invoice_date)}</span>
                    <span>{formatInr(inv.grand_total)}</span>
                    <span className={`recurring-detail__inv-status recurring-detail__inv-status--${String(inv.status || "").toLowerCase()}`}>
                      {inv.status || "—"}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>

        <footer className="recurring-detail__footer">
          <button
            type="button"
            className="quotes-page__action-btn quotes-page__action-btn--primary"
            disabled={busy}
            onClick={() => onRunNow(template)}
          >
            <Play size={14} />
            Run now
          </button>
          {template.status === "active" ? (
            <button type="button" className="quotes-page__action-btn" disabled={busy} onClick={() => onPause(template)}>
              <Pause size={14} />
              Pause
            </button>
          ) : template.status === "paused" ? (
            <button type="button" className="quotes-page__action-btn" disabled={busy} onClick={() => onResume(template)}>
              <Repeat size={14} />
              Resume
            </button>
          ) : null}
          {template.missed_runs > 0 && template.status === "active" && (
            <button
              type="button"
              className="quotes-page__action-btn"
              disabled={busy}
              onClick={() => onCatchUp(template)}
            >
              <FastForward size={14} />
              Catch up
            </button>
          )}
          <button
            type="button"
            className="quotes-page__action-btn"
            onClick={() => navigate(`/recurring-invoices/edit/${template.id}`)}
          >
            <Pencil size={14} />
            Edit
          </button>
          <button
            type="button"
            className="quotes-page__action-btn recurring-detail__delete"
            disabled={busy}
            onClick={() => onDelete(template)}
          >
            <Trash2 size={14} />
            Delete
          </button>
        </footer>
      </aside>
    </>
  );
}
